import { useState } from "react"
import styled from "styled-components"

import {
  Banner,
  breakpoint,
  colors,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"
import { TextField } from "@minuthq/meatball-ui-react"
import { useDebouncedCallback } from "use-debounce"

import { FormInput } from "src/components/HoneyPot/FormInput"
import { ImageBackdrop } from "src/components/ImageBackdrop/ImageBackdrop"
import { MarketingConsentCheckbox } from "src/components/Signup/MarketingConsentCheckbox"
import { TosCheckbox } from "src/components/Signup/TosCheckbox"
import { usePostPasswordValidity } from "src/data/user/queries/userQueries"
import { useTranslate } from "src/i18n/useTranslate"
import { DocumentHead } from "src/router/DocumentHead"
import { getInviteCodeFromUrl } from "src/router/organizationRoutes"
import { MButton } from "src/ui/Button/MButton"
import { Divider } from "src/ui/Divider/Divider"
import Logo from "src/ui/icons/logo.svg"
import backdropImage from "src/ui/images/photo-building.jpg"
import { InternalLink } from "src/ui/Link/InternalLink"
import { MCard } from "src/ui/MCard/MCard"
import { MCircularProgress } from "src/ui/MCircularProgress/MCircularProgress"

export interface ICreateAccountSubmit {
  name: string
  email: string
  password: string
  subscribe_newsletter: boolean
  honey: string
}
export function CreateAccount({
  onSubmit: handleSubmit,
  creatingAccount,
  error,
  prefillEmail,
}: {
  onSubmit: (accountConf: ICreateAccountSubmit) => void
  creatingAccount: boolean
  error: React.ReactNode
  prefillEmail?: string
}) {
  const { t, langKeys } = useTranslate()

  const [email, setEmail] = useState(prefillEmail ?? "")
  const [name, setName] = useState("")
  const [password, setPassword] = useState("")
  const [tosAccepted, setTosAccepted] = useState(false)
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false)
  const [honey, setHoney] = useState("")

  const [passwordError, setPasswordError] = useState({
    isError: false,
    message: "",
  })

  const [tosError, setTosError] = useState(false)

  const inviteCode = getInviteCodeFromUrl()

  const postPasswordValidity = usePostPasswordValidity()
  const debouncedPostPasswordValidity = useDebouncedCallback(
    (password: string) => {
      postPasswordValidity.mutate(
        { password },
        {
          onError: (error) => {
            if (error.response?.data.context?.warning) {
              setPasswordError({
                isError: true,
                message: error.response.data.context.warning,
              })
            } else {
              setPasswordError({
                isError: true,
                message: t(langKeys.password_too_weak),
              })
            }
          },
        }
      )
    },
    500
  )

  function handleSetPassword(password: string) {
    if (password.length < 8) {
      setPasswordError({
        isError: true,
        message: t(langKeys.password_too_short),
      })
    } else if (password.length > 50) {
      setPasswordError({
        isError: true,
        message: t(langKeys.password_too_long),
      })
    } else {
      setPasswordError({
        isError: false,
        message: "",
      })
      debouncedPostPasswordValidity(password)
    }
    setPassword(password)
  }

  const title = !!inviteCode
    ? t(langKeys.organizations_team_join_create_account)
    : t(langKeys.create_account)

  return (
    <div>
      <DocumentHead
        title={t(langKeys.sign_up_meta_title)}
        metaTags={[
          {
            name: "description",
            content: t(langKeys.sign_up_meta_description),
          },
        ]}
      />

      <ImageBackdrop
        landscapeImageUrl={backdropImage}
        portraitImageUrl={backdropImage}
        backgroundColor={colors.neutral}
      >
        <CreateAccountCard>
          <LogoBox>
            <Logo width={90} />
          </LogoBox>

          <Text variant="heading1" marginBottom={spacing.XL}>
            {title}
          </Text>

          <form
            onSubmit={(e) => {
              e.preventDefault()

              if (passwordError.isError) {
                return
              }

              if (!tosAccepted) {
                setTosError(true)
                return
              }

              handleSubmit({
                name,
                email,
                password,
                honey,
                subscribe_newsletter: subscribeNewsletter,
              })
            }}
          >
            <InputsWrapper>
              <TextField
                id="fullName"
                label={t(langKeys.full_name)}
                type="text"
                autoFocus
                required
                value={name}
                onChange={(value) => setName(value)}
                onBlur={(e) => setName(e.target.value.trim())}
              />
              <TextField
                id="email"
                label={t(langKeys.email)}
                type="email"
                required
                value={email}
                onChange={(value) => setEmail(value)}
                onBlur={(e) =>
                  setEmail(e.target.value.toLocaleLowerCase().trim())
                }
                disabled={!!prefillEmail}
              />

              <TextField
                id="password"
                label={t(langKeys.password)}
                type="password"
                required
                error={
                  passwordError.isError ? passwordError.message : undefined
                }
                value={password}
                onChange={(value) => handleSetPassword(value)}
                endAdornment={
                  postPasswordValidity.isLoading ||
                  debouncedPostPasswordValidity.isPending() ? (
                    <MCircularProgress color="inherit" size={20} />
                  ) : (
                    <></>
                  )
                }
              />
            </InputsWrapper>

            <FormInput value={honey} onChange={setHoney} />

            <ConsentWrapper>
              <MarketingConsentCheckbox
                checked={subscribeNewsletter}
                onChecked={(checked) => setSubscribeNewsletter(checked)}
              />

              <TosCheckbox
                checked={tosAccepted}
                onChecked={(checked) => setTosAccepted(checked)}
                error={tosError}
              />
            </ConsentWrapper>

            <FormFooter>
              {!!error && (
                <Banner type="error" fullWidth>
                  {error}
                </Banner>
              )}

              <div>
                <MButton type="submit" loading={creatingAccount}>
                  {t(langKeys.create_an_account)}
                </MButton>
              </div>
            </FormFooter>
          </form>

          <Divider $margin={spacing.XL} />

          {!prefillEmail && (
            <Text variant="bodyS" color="secondary">
              {t(langKeys.sign_up_redirect_to_sign_in)}{" "}
              <InternalLink to={`/login${window.location.search}`}>
                {t(langKeys.sign_in)}
              </InternalLink>
            </Text>
          )}
        </CreateAccountCard>
      </ImageBackdrop>
    </div>
  )
}

const LogoBox = styled.div`
  margin-bottom: ${spacing.XL};
`

const InputsWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
  margin-bottom: ${spacing.M};
`

const ConsentWrapper = styled.div`
  display: grid;
  gap: ${spacing.S};
`

const FormFooter = styled.div`
  display: grid;
  gap: ${spacing.M};
  margin-top: ${spacing.XL};
`

const CreateAccountCard = styled(MCard)`
  padding: ${spacing.L};
  max-width: 65ch;

  @media (${breakpoint.mediumUp}) {
    padding: ${spacing.XL2};
  }
`
